import React, { useState } from 'react';
import LegacyPratusTable from '../../../components/DesignSystems/PratusTable/LegacyPratusTable';
import type { ColumnDescription } from 'react-bootstrap-table-next';
import { useFormContext } from 'react-hook-form';
import { Link } from 'react-router';
import { StylishNewButton } from '../../../components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from '../../../components/SharedIcon/SharedIcon';
import CIREditDrawer from '../CIREditDrawer';
import { DGPTAlertCIR } from '../../../types/dgptAlerts';
import { useUpdateAlertConfig } from '../hooks/useAlertConfigs';
import { toast } from 'react-toastify';

const CIRsStep: React.FC = () => {
  const form = useFormContext();
  const { setValue, watch, getValues } = form;
  const updateConfig = useUpdateAlertConfig();

  const [showDrawer, setShowDrawer] = useState(false);
  const [editingCIR, setEditingCIR] = useState<DGPTAlertCIR | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const cirs = watch('cirs') || [];

  const handleAddCIR = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    setEditingCIR(null);
    setEditingIndex(null);
    setShowDrawer(true);
  };

  const handleEditCIR = (cir: DGPTAlertCIR, index: number) => {
    setEditingCIR(cir);
    setEditingIndex(index);
    setShowDrawer(true);
  };

  const handleDeleteCIR = (index: number) => {
    const updatedCIRs = [...cirs];
    updatedCIRs.splice(index, 1);
    setValue('cirs', updatedCIRs);
    saveCIRsToConfig(updatedCIRs);
  };

  const handleSaveCIR = (cir: DGPTAlertCIR) => {
    const updatedCIRs = [...cirs];

    if (editingIndex !== null) {
      // Update existing CIR
      updatedCIRs[editingIndex] = cir;
    } else {
      // Add new CIR
      updatedCIRs.push(cir);
    }

    setValue('cirs', updatedCIRs);
    saveCIRsToConfig(updatedCIRs);
    setShowDrawer(false);
    setEditingCIR(null);
    setEditingIndex(null);
  };

  const saveCIRsToConfig = async (updatedCIRs: DGPTAlertCIR[]) => {
    try {
      const currentConfig = getValues();

      // Only save to backend if the configuration already exists (has an ID)
      if (!currentConfig.id) {
        // Configuration doesn't exist yet, just update the form state
        return;
      }

      // Clean up the CIRs to ensure consistent structure
      const cleanedCIRs = updatedCIRs.map((cir) => ({
        name: cir.name,
        prompt: cir.prompt,
        data_sources: cir.data_sources.map((ds) => ({
          source: ds.source,
          id: ds.id || '',
        })),
      }));

      await updateConfig.mutateAsync({
        id: currentConfig.id,
        cirs: cleanedCIRs,
      });
    } catch (error) {
      console.error('Error saving CIRs:', error);
      toast.error('Failed to save CIRs.');
    }
  };

  const getDataSourceDescription = (source: string): string => {
    const descriptions: Record<string, string> = {
      News: 'News Articles',
      Files: 'Files uploaded to Pratus',
      Web: 'Web Scraping',
      'Current Weather': 'Current weather at location',
      'Forecast Weather': 'Forecast weather at location',
      'ICS-Active': 'Current ICS data',
      'ICS-Historic': 'Full version history of ICS data',
      'NEO Analysis': 'Noncombatant Evacuation Order Analysis',
    };
    return descriptions[source] || source;
  };

  const tableColumns: ColumnDescription[] = [
    {
      dataField: 'name',
      text: 'Name',
      sort: true,
    },
    {
      dataField: 'prompt',
      text: 'AI Instructions',
      sort: true,
      formatter: (cell: string) => cell || 'No instructions provided',
    },
    {
      dataField: 'data_sources',
      text: 'Data Sources',
      sort: true,
      formatter: (cell: Array<{ source: string; id: string }>) =>
        cell
          ?.map((ds: { source: string; id: string }) =>
            getDataSourceDescription(ds.source)
          )
          .join(', '),
    },
    {
      dataField: 'actions',
      text: 'Actions',
      sort: false,
      isDummyField: true,
      formatter: (_cell: any, row: DGPTAlertCIR, index: number) => (
        <div className="d-flex gap-2">
          <StylishNewButton
            className="button--secondary button--icon"
            onClick={() => handleEditCIR(row, index)}
          >
            <SharedIcon iconName="edit" size="sm" />
          </StylishNewButton>
          <StylishNewButton
            className="button--secondary button--icon"
            onClick={() => handleDeleteCIR(index)}
          >
            <SharedIcon iconName="delete" size="sm" />
          </StylishNewButton>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between mb-2">
        <h6>Critical Information Requirements (CIRs)</h6>
        <StylishNewButton className="button--primary" onClick={handleAddCIR}>
          Add CIR
        </StylishNewButton>
      </div>
      <p className="text-muted mb-4">
        Critical Information Requirements (CIRs) define the information needed
        for the <Link to="/sitreps">PRATUS AI SITREP</Link>. Each CIR specifies
        what data to collect and how to analyze it, with prompts guiding the AI
        in gathering relevant information from the selected data sources.
      </p>

      {cirs.length === 0 && (
        <div className="alert alert-warning mb-4">
          <strong>At least one CIR is required.</strong> Please add a CIR to
          continue to the next step.
        </div>
      )}

      <LegacyPratusTable
        keyField="name"
        rows={cirs}
        columns={tableColumns}
        noDataIndication="No CIRs configured. Click 'Add CIR' to get started."
      />

      {showDrawer && (
        <CIREditDrawer
          show={showDrawer}
          onHide={() => {
            setShowDrawer(false);
            setEditingCIR(null);
            setEditingIndex(null);
          }}
          onSave={handleSaveCIR}
          cir={editingCIR}
        />
      )}
    </div>
  );
};

export default CIRsStep;
